import { AlertTriangle, CheckCircle2, Mail } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '../store'
import Spinner from '../components/Spinner'
import { login } from '../components/UserMenu'
import { SUPPORT_EMAIL } from '../lib/contact'
import './VerifyEmailPage.css'

type VerificationResult = 'success' | 'failure' | 'none'

// Auth0 reports the outcome of the email-verification click through redirect params: an explicit
// success flag, the code=success shortcut, or (legacy) a message we sniff for "verified" wording.
// success=false is an explicit failure — e.g. "This URL can be used only once" for an
// already-consumed link — and must be surfaced, not collapsed into the neutral instructions.
function getVerificationResult(searchParams: URLSearchParams): VerificationResult {
  const success = searchParams.get('success')
  if (success === 'true') return 'success'
  if (success === 'false') return 'failure'
  if (searchParams.get('code') === 'success') return 'success'
  const message = searchParams.get('message')?.toLowerCase() ?? ''
  if (message.includes('email was verified') || message.includes('email verified')) return 'success'
  return 'none'
}

// Shown when Auth0 denies login (check your email), after clicking the verification link
// (success), or when the verification link fails (e.g. it was already used).
export default function VerifyEmailPage() {
  const dispatch = useDispatch<AppDispatch>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo') || '/'
  const result = getVerificationResult(searchParams)
  const verified = result === 'success'
  const failed = result === 'failure'
  const failureMessage = searchParams.get('message') ?? ''

  // Auth0's logout hop lands back here with signIn=1 to resume the login it just cleared the session
  // for. Reading it from the URL means the redirecting state renders on the first paint, so the card
  // below never appears on the way to the login screen.
  const resumeSignIn = searchParams.get('signIn') === '1'
  const [leavingToSignIn, setLeavingToSignIn] = useState(resumeSignIn)

  useEffect(() => {
    if (!resumeSignIn) return
    login(dispatch, returnTo, { prompt: 'login' })
  }, [dispatch, resumeSignIn, returnTo])

  if (leavingToSignIn) {
    return (
      <div className="verify-email-page">
        <div className="verify-email-page__card" data-testid="verify-email-redirecting">
          <div className="verify-email-page__brand">
            <img src="/images/logo.svg" alt="" width="26" height="30" className="verify-email-page__logo" />
            <span className="verify-email-page__wordmark">Replay QA</span>
          </div>
          <p className="verify-email-page__lead verify-email-page__redirecting">
            <Spinner size="md" /> Taking you to sign in…
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="verify-email-page">
      <div className="verify-email-page__card" data-testid="verify-email-page">
        <div className="verify-email-page__brand">
          <img src="/images/logo.svg" alt="" width="26" height="30" className="verify-email-page__logo" />
          <span className="verify-email-page__wordmark">Replay QA</span>
        </div>

        <div className="verify-email-page__icon" aria-hidden>
          {failed ? (
            <AlertTriangle size={24} strokeWidth={1.75} />
          ) : verified ? (
            <CheckCircle2 size={24} strokeWidth={1.75} />
          ) : (
            <Mail size={24} strokeWidth={1.75} />
          )}
        </div>

        <h1 className="verify-email-page__title">
          {failed ? 'Email verification failed' : verified ? 'Email verified' : 'Check your email'}
        </h1>

        {failed ? (
          <>
            <p className="verify-email-page__lead">
              This verification link is invalid or has already been used. Each verification link
              works only once.
            </p>
            <div className="verify-email-page__error" role="alert" data-testid="verify-email-error">
              {failureMessage || 'The verification request failed.'}
            </div>
          </>
        ) : (
          <p className="verify-email-page__lead">
            {verified
              ? 'Your email address is confirmed. Sign in to finish setting up your Replay QA account.'
              : 'We sent you a verification link. Open it to confirm your email address, then come back here and sign in.'}
          </p>
        )}

        {!verified && !failed && (
          <div className="verify-email-page__steps">
            <ol>
              <li>Check your inbox for an email from Replay.</li>
              <li>Click <strong>Verify email address</strong> in that email.</li>
              <li>Return here and sign in with the same email and password.</li>
            </ol>
          </div>
        )}

        <button
          type="button"
          className="verify-email-page__btn"
          data-testid="verify-email-sign-in"
          onClick={() => {
            setLeavingToSignIn(true)
            login(dispatch, returnTo, { prompt: 'login', freshSession: true })
          }}
        >
          {verified ? 'Sign in' : 'Back to sign in'}
        </button>

        {!verified && !failed && (
          <p className="verify-email-page__footer">
            Didn&apos;t get the email? Check spam and wait a few minutes. Once you&apos;ve clicked the
            link, use <strong>Back to sign in</strong> above. Questions?{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        )}

        {failed && (
          <p className="verify-email-page__footer">
            If your email still needs verification, use <strong>Back to sign in</strong> above.
            Questions? <a href="mailto:support@replay.io">support@replay.io</a>
          </p>
        )}
      </div>
    </div>
  )
}
